import { AggregateType } from "~/enums/aggregateType";
import { ErrorType } from "~/enums/ErrorType";
import { ObjectModelPropertyRootType } from "~/enums/objectModelPropertyType";
import scrollToElement from "~/helpers/scrollToElement";
import { StringHelper } from "~/helpers/stringHelper";
import { ValidationModalSection } from "~/models/navigableValidations/validationSections";
import ReferenceableValidator from "~/models/navigableValidators/referenceableValidator";
import { IErrorNavOpts, ValidationErrorLevel } from "~/models/validationError";
import FieldPresenceValidator from "../navigableValidators/fieldPresenceValidator";
import LinkedRelationMappedPropertiesValidator from "../navigableValidators/linkedRelationMappedPropertiesValidator";
import NavigableValidator from "../navigableValidators/navigableValidator";
import ObjectModelPropertiesValidator from "../navigableValidators/objectModelPropertiesValidator";
import ObjectModelRelationshipPresenceValidator from "../navigableValidators/objectModelRelationshipPresenceValidator";
import ObjectModelProperty from "../objectModelProperties/objectModelProperty";
import NavigableValidation from "./navigableValidation";

export default class ObjectModelPropertyValidation extends NavigableValidation<ObjectModelProperty> {
  model: ObjectModelProperty;
  _validators: NavigableValidator<ObjectModelProperty>[] =[
    new ObjectModelPropertiesValidator({
      validatorKey: 'object_model_properties',
      section: ValidationModalSection.Properties
    }),
    this.blankLabelTitle(),
  ];

  get validators() {
    if (this.model.property_type === ObjectModelPropertyRootType.MappedProperty || this.model.property_type === ObjectModelPropertyRootType.Aggregate || this.model.property_type === ObjectModelPropertyRootType.RelationshipCount) {
      if (!this.hasValidatorFor('linked_relation_client_id')) {
        this._validators.push(new ObjectModelRelationshipPresenceValidator({
          validatorKey: 'linked_relation_client_id',
          section: ValidationModalSection.Properties
        }));
      }
      if (!this.hasValidatorFor('linked_relation_mapped_properties') && this.model.aggregate_type !== AggregateType.Count && this.model.property_type !== ObjectModelPropertyRootType.RelationshipCount) {
        this._validators.push(new LinkedRelationMappedPropertiesValidator({
          section: ValidationModalSection.Properties
        }));
      }
    }
    if (this.model.property_type === ObjectModelPropertyRootType.Aggregate){
      if (!this.hasValidatorFor('aggregate_type')) {
        this._validators.push(new FieldPresenceValidator({
          validatorKey: 'aggregate_type',
          displayName: "Aggregate Function",
          section: ValidationModalSection.Properties
        }));
      }
    }

    if (!this.hasValidatorFor('reference_name')) {
      this._validators.push(new ReferenceableValidator({
        section: ValidationModalSection.Properties
      }));
    }
    return this._validators;
  }

  hasValidatorFor(field: string): boolean {
    return this._validators.some(it => it.field === field);
  }

  errorNavOpts(validator, field: string, value?): IErrorNavOpts {
    let base = super.errorNavOpts(validator, field, value)
    Object.assign(base, {
      query: {selectedId: this.model.client_id},
      targetElementId: this.model.idForProp(field),
    });
    return base;
  }

  blankLabelTitle(): NavigableValidator<ObjectModelProperty> {
    return new NavigableValidator(Object.assign({
      path: "properties",
      displayName: (validatorKey, property) => `Label not configured on ${property.displayName}`,
      beforeReveal: async () => {
        await window.requestAnimationFrame(async () => {
          await this.scrollToAndSelectBlankLabel();
        })
      },
      containerElementSelector: ".scroll-area.work-area",
      errorLevel: ValidationErrorLevel.Warning,
      validatorKey: "blankLabelTitle",
      section: ValidationModalSection.Properties,
      validator: (value, model: ObjectModelProperty) => {
        return model.label_title && StringHelper.isPresent(model.label_title)
      },
      message: "The property is blank",
      type: ErrorType.presentWarning,
    }))
  }

  async scrollToAndSelectBlankLabel(): Promise<JQuery> {
    const $label = $(`#scroll-to-label-${this.model.client_id}`);
    await scrollToElement({
      $el: $label,
      $container: $("div.scroll-area.work-area")
    })

    return $label;
  }
}